import { useNavigationHistoryStore } from '@/store/useNavigationHistoryStore'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'

/**
 * history.state から Next.js Pages Router の履歴キーを読む（#3665）。
 *
 * Pages Router は changeState() で history.state に { url, as, options, __N: true, key } を書く（内部仕様）。
 * 形が変わって key が取れないときは null を返し、呼び出し側は「戻り先なし → HOME」の安全側に倒す。
 */
export const readHistoryKey = (state: unknown): string | null => {
  if (typeof state !== 'object' || state === null) return null
  const key = (state as { key?: unknown }).key
  return typeof key === 'string' ? key : null
}

const readCurrentHistoryKey = (): string | null =>
  typeof window === 'undefined' ? null : readHistoryKey(window.history.state)

/**
 * フルロードしたページの 1 つ前の履歴エントリが、同じアプリ（同一オリジン・同一キャンペーン）のページかを判定する（#3665）。
 *
 * リファラが同じアプリ内なら、そのページから push 型の遷移で来ている（＝ブラウザバックでそこへ戻れる）とみなす。
 * 新しいタブで開いた場合はリファラがあっても戻れないため、履歴が 1 件しかないときは false にする。
 * 認証サーバー・決済・外部サイトからの流入はオリジンが違うため false になり、戻るは HOME へ倒れる。
 *
 * @param withoutBasePath スラッグなし構成（NEXT_PUBLIC_WITHOUT_BASE_PATH=1）か。true のときは先頭セグメントを比較しない
 */
export const hasInAppPreviousEntry = ({
  referrer,
  currentUrl,
  historyLength,
  withoutBasePath,
}: {
  referrer: string
  currentUrl: string
  historyLength: number
  withoutBasePath: boolean
}): boolean => {
  if (!referrer || historyLength <= 1) return false
  let from: URL
  let current: URL
  try {
    from = new URL(referrer)
    current = new URL(currentUrl)
  } catch {
    return false
  }
  if (from.origin !== current.origin) return false
  if (withoutBasePath) return true
  const slugOf = (url: URL) => url.pathname.split('/')[1] ?? ''
  return slugOf(from) !== '' && slugOf(from) === slugOf(current)
}

/**
 * アプリ内遷移の履歴キー列を useNavigationHistoryStore に記録する。
 * _app（AppContent）で 1 回だけ呼ぶ。マウント = フルロードなので、現在のエントリだけを持つ状態から始める。
 * raw な window.history.pushState（オーバーレイの履歴マーカー）は key を持たないため記録されない。
 *
 * routeChangeComplete の `shallow` は判定に使わない。Next.js は popstate 時に
 * 「復元先エントリの shallow && 直前の遷移の shallow」を渡すため、ページが変わる戻る操作でも true になり得る。
 */
export const useTrackNavigationHistory = (): void => {
  const router = useRouter()
  const record = useNavigationHistoryStore((s) => s.record)
  const reset = useNavigationHistoryStore((s) => s.reset)
  // 直近の popstate（戻る / 進む）で復元されたエントリの履歴キー。
  // routeChangeComplete 時のキーと一致すれば、その遷移は push ではなく戻る / 進むだと分かる
  const poppedKeyRef = useRef<string | null>(null)

  useEffect(() => {
    reset(
      readCurrentHistoryKey(),
      hasInAppPreviousEntry({
        referrer: document.referrer,
        currentUrl: window.location.href,
        historyLength: window.history.length,
        withoutBasePath: process.env.NEXT_PUBLIC_WITHOUT_BASE_PATH === '1',
      }),
    )
  }, [reset])

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      poppedKeyRef.current = readHistoryKey(event.state)
    }
    const handleRouteChange = () => {
      const key = readCurrentHistoryKey()
      const viaPopState = key !== null && poppedKeyRef.current === key
      poppedKeyRef.current = null
      if (key !== null) record(key, viaPopState)
    }
    window.addEventListener('popstate', handlePopState)
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      window.removeEventListener('popstate', handlePopState)
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events, record])
}
